import React, { useState, useRef, Fragment } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import {
  CloudArrowUpIcon,
  DocumentIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
  EyeIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';
import { Dialog, Transition } from '@headlessui/react';
import ConfirmationModal from './ConfirmationModal';
import mammoth from 'mammoth';
import * as XLSX from 'xlsx';
import { sanitizeHtml } from '../utils/sanitize';
import { uploadAttachmentsDirect } from '../utils/directAttachmentUpload';

interface Attachment {
  id: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  createdAt: string;
  storagePath?: string;
  downloadUrl?: string; // SAS URL for direct download (Azure storage)
  storageType?: string;
  User: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface AttachmentTabProps {
  entityType: 'alert' | 'case';
  entityId: string;
  canUpload?: boolean;
  canDelete?: boolean;
  /** When true, hides the upload dropzone entirely (read-only list). */
  hideUploadArea?: boolean;
  /** Override empty-state copy. */
  emptyMessage?: string;
  ticketState?: string; // Current state of the ticket (e.g., 'closed', 'cancelled')
  onAttachmentAdded?: () => void; // Optional callback when attachment is added
  onAttachmentDeleted?: () => void; // Optional callback when attachment is deleted
}

const AttachmentTab: React.FC<AttachmentTabProps> = ({
  entityType,
  entityId,
  canUpload = true,
  canDelete = true,
  hideUploadArea = false,
  emptyMessage,
  ticketState,
  onAttachmentAdded,
  onAttachmentDeleted
}) => {
  // Check if ticket is closed, cancelled, resolved, or auto-resolved
  const isTicketClosedOrCancelled = ticketState === 'closed' || ticketState === 'cancelled' || ticketState === 'resolved' || ticketState === 'auto_resolved';
  // Disable uploads if ticket is closed/cancelled/auto-resolved
  const effectiveCanUpload = canUpload && !isTicketClosedOrCancelled;
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState<Attachment | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [attachmentToPreview, setAttachmentToPreview] = useState<Attachment | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewContent, setPreviewContent] = useState<string | null>(null);

  const entityPath = entityType === 'alert' ? 'alerts' : 'cases';

  // Fetch attachments
  const { data, isLoading, isError } = useQuery<{ attachments: Attachment[] }>({
    queryKey: [`${entityType}-attachments`, entityId],
    queryFn: async () => {
      const response = await api.get(`/${entityPath}/${entityId}/attachments`);
      return response.data;
    },
    enabled: Boolean(entityId),
  });

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (files: FileList) => {
      const uploaded = await uploadAttachmentsDirect(
        api,
        `/${entityPath}/${entityId}/attachments`,
        files
      );
      return { success: true, attachments: uploaded };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`${entityType}-attachments`, entityId] });
      // Invalidate activities for cases to show attachment activity
      if (entityType === 'case') {
        queryClient.invalidateQueries({ queryKey: ['case-activities', entityId] });
      }
      setUploadError(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      // Call callback if provided
      if (onAttachmentAdded) {
        onAttachmentAdded();
      }
    },
    onError: (error: any) => {
      setUploadError(error.response?.data?.error || 'Failed to upload files');
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (attachmentId: string) => {
      await api.delete(`/${entityPath}/${entityId}/attachments/${attachmentId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`${entityType}-attachments`, entityId] });
      // Invalidate activities for cases to show attachment activity
      if (entityType === 'case') {
        queryClient.invalidateQueries({ queryKey: ['case-activities', entityId] });
      }
      // Call callback if provided
      if (onAttachmentDeleted) {
        onAttachmentDeleted();
      }
    }
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      uploadMutation.mutate(e.target.files);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      uploadMutation.mutate(e.dataTransfer.files);
    }
  };

  const handleDownload = async (attachmentId: string, filename: string, downloadUrl?: string) => {
    try {
      // If downloadUrl is provided (Azure with SAS token), use direct download
      if (downloadUrl) {
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', filename);
        link.setAttribute('target', '_blank'); // Open in new tab if direct download fails
        document.body.appendChild(link);
        link.click();
        link.remove();
        return;
      }

      // Fallback to backend proxy download
      const response = await api.get(
        `/${entityPath}/${entityId}/attachments/${attachmentId}/download`,
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  const handleDeleteClick = (attachment: Attachment) => {
    setAttachmentToDelete(attachment);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (attachmentToDelete) {
      deleteMutation.mutate(attachmentToDelete.id);
      setDeleteModalOpen(false);
      setAttachmentToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setAttachmentToDelete(null);
  };

  const handlePreviewClick = async (attachment: Attachment) => {
    setAttachmentToPreview(attachment);
    setPreviewModalOpen(true);
    setPreviewLoading(true);
    setPreviewContent(null);

    try {
      let blob: Blob;

      // If downloadUrl is provided (Azure with SAS token), fetch directly
      if (attachment.downloadUrl) {
        const response = await fetch(attachment.downloadUrl);
        blob = await response.blob();
      } else {
        // Fallback to backend proxy
        const response = await api.get(
          `/${entityPath}/${entityId}/attachments/${attachment.id}/download`,
          { responseType: 'blob' }
        );
        blob = response.data;
      }

      // Create blob with correct MIME type
      blob = new Blob([blob], { type: attachment.mimeType });

      // Handle CSV files
      if (attachment.mimeType === 'text/csv' || attachment.originalName.toLowerCase().endsWith('.csv')) {
        const text = await blob.text();
        const workbook = XLSX.read(text, { type: 'string' });

        // Convert CSV to HTML table
        let html = '';
        workbook.SheetNames.forEach((sheetName) => {
          const worksheet = workbook.Sheets[sheetName];
          html += XLSX.utils.sheet_to_html(worksheet, { header: '', footer: '' });
        });
        setPreviewContent(html);
        setPreviewLoading(false);
        return;
      }

      // Handle Office documents
      if (isOfficeDocument(attachment.mimeType)) {
        if (attachment.mimeType.includes('word')) {
          // Parse DOCX
          const arrayBuffer = await blob.arrayBuffer();
          const result = await mammoth.convertToHtml({ arrayBuffer });
          setPreviewContent(result.value);
        } else if (attachment.mimeType.includes('spreadsheet') || attachment.mimeType.includes('excel')) {
          // Parse XLSX
          const arrayBuffer = await blob.arrayBuffer();
          const workbook = XLSX.read(arrayBuffer, { type: 'array' });

          // Convert all sheets to HTML
          let html = '';
          workbook.SheetNames.forEach((sheetName) => {
            const worksheet = workbook.Sheets[sheetName];
            html += `<h3 style="margin: 20px 0 10px 0; color: #1f2937; font-size: 1.1rem; font-weight: 600;">${sheetName}</h3>`;
            html += XLSX.utils.sheet_to_html(worksheet, { header: '', footer: '' });
          });
          setPreviewContent(html);
        }
        setPreviewLoading(false);
        return;
      }

      // For other file types, create a blob URL with correct MIME type
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (error) {
      console.error('Preview failed:', error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePreviewClose = () => {
    setPreviewModalOpen(false);
    setAttachmentToPreview(null);
    setPreviewContent(null);
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const isOfficeDocument = (mimeType: string): boolean => {
    const officeTypes = [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // XLSX
      'application/vnd.openxmlformats-officedocument.presentationml.presentation', // PPTX
      'application/msword', // DOC
      'application/vnd.ms-excel', // XLS
      'application/vnd.ms-powerpoint' // PPT
    ];
    return officeTypes.includes(mimeType);
  };

  const isCsvFile = (mimeType: string, filename: string): boolean => {
    return mimeType === 'text/csv' || filename.toLowerCase().endsWith('.csv');
  };

  const isPreviewable = (mimeType: string): boolean => {
    return (
      mimeType.startsWith('image/') ||
      mimeType === 'application/pdf' ||
      mimeType.startsWith('text/') ||
      mimeType === 'application/json' ||
      isOfficeDocument(mimeType)
    );
  };

  const renderPreview = () => {
    if (!attachmentToPreview) return null;

    const { mimeType, originalName } = attachmentToPreview;

    // Handle CSV files first (they use previewContent)
    if (isCsvFile(mimeType, originalName) && previewContent) {
      return (
        <div className="w-full h-[70vh] overflow-auto bg-white p-4">
          <style>{`
            .preview-csv table {
              border-collapse: collapse;
              width: 100%;
              font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
              font-size: 11px;
            }
            .preview-csv td, .preview-csv th {
              border: 1px solid #d0d0d0;
              padding: 4px 8px;
              text-align: left;
              background-color: white;
              min-width: 60px;
            }
            .preview-csv th {
              background-color: #f0f0f0;
              font-weight: 600;
            }
            .preview-csv tr:hover td {
              background-color: #f5f5f5;
            }
          `}</style>
          <div
            className="preview-csv"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewContent) }}
          />
        </div>
      );
    }

    // Handle Office documents (they use previewContent, not previewUrl)
    if (isOfficeDocument(mimeType)) {
      // If we have parsed content, display it with appropriate styling
      if (previewContent) {
        // Word document viewer - styled like a document
        if (mimeType.includes('word')) {
          return (
            <div className="w-full h-[70vh] overflow-auto bg-gray-100 p-8">
              <div
                className="max-w-4xl mx-auto bg-white shadow-lg p-12"
                style={{
                  minHeight: '100%',
                  fontFamily: '"Calibri", "Arial", sans-serif',
                  fontSize: '11pt',
                  lineHeight: '1.6',
                  color: '#000000'
                }}
              >
                <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewContent) }} />
              </div>
            </div>
          );
        }

        // Excel spreadsheet viewer - styled like a spreadsheet
        if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) {
          return (
            <div className="w-full h-[70vh] overflow-auto bg-white p-4">
              <style>{`
                .preview-excel table {
                  border-collapse: collapse;
                  width: 100%;
                  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
                  font-size: 11px;
                  margin-bottom: 30px;
                }
                .preview-excel td, .preview-excel th {
                  border: 1px solid #d0d0d0;
                  padding: 4px 8px;
                  text-align: left;
                  background-color: white;
                  min-width: 60px;
                }
                .preview-excel th {
                  background-color: #f0f0f0;
                  font-weight: 600;
                }
                .preview-excel tr:hover td {
                  background-color: #f5f5f5;
                }
              `}</style>
              <div
                className="preview-excel"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewContent) }}
              />
            </div>
          );
        }

        // PowerPoint viewer (basic HTML display)
        if (mimeType.includes('presentation')) {
          return (
            <div className="w-full h-[70vh] overflow-auto bg-gray-900 p-8">
              <div
                className="max-w-5xl mx-auto bg-white p-8"
                style={{
                  fontFamily: '"Calibri", "Arial", sans-serif',
                  fontSize: '14pt'
                }}
              >
                <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewContent) }} />
              </div>
            </div>
          );
        }

        // Fallback for other Office formats
        return (
          <div
            className="w-full h-[70vh] overflow-auto bg-white p-6"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewContent) }}
            style={{
              fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            }}
          />
        );
      }

      // Fallback if parsing failed
      return (
        <div className="text-center py-12">
          <div className="mx-auto h-24 w-24 mb-4 flex items-center justify-center bg-blue-100 rounded-lg">
            <svg className="h-16 w-16 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Unable to preview this file
          </h3>
          <p className="text-sm text-gray-500 mb-6">
            Download the file to view it in your preferred application.
          </p>
          <button
            onClick={() => handleDownload(attachmentToPreview.id, attachmentToPreview.originalName, attachmentToPreview.downloadUrl)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
            Download File
          </button>
        </div>
      );
    }

    // For other file types, check if previewUrl is available
    if (!previewUrl) {
      return (
        <div className="text-center text-gray-500 py-8">
          Loading file...
        </div>
      );
    }

    if (mimeType.startsWith('image/')) {
      return (
        <img
          src={previewUrl}
          alt={attachmentToPreview.originalName}
          className="max-w-full max-h-[70vh] mx-auto"
        />
      );
    }

    if (mimeType === 'application/pdf') {
      return (
        <iframe
          src={previewUrl}
          className="w-full h-[70vh]"
          title={attachmentToPreview.originalName}
        />
      );
    }

    if (mimeType.startsWith('text/') || mimeType === 'application/json') {
      return (
        <iframe
          src={previewUrl}
          className="w-full h-[70vh] bg-white"
          title={attachmentToPreview.originalName}
        />
      );
    }

    // For non-previewable files, show file information
    return (
      <div className="text-center py-12">
        <DocumentIcon className="mx-auto h-24 w-24 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Preview not available
        </h3>
        <p className="text-sm text-gray-500 mb-1">
          File type: {mimeType}
        </p>
        <p className="text-sm text-gray-500 mb-6">
          Size: {formatFileSize(attachmentToPreview.size)}
        </p>
        <button
          onClick={() => handleDownload(attachmentToPreview.id, attachmentToPreview.originalName, attachmentToPreview.downloadUrl)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
          Download File
        </button>
      </div>
    );
  };

  const resolvedEmptyMessage =
    emptyMessage ??
    (hideUploadArea
      ? 'No attachments found.'
      : `No attachments yet.${effectiveCanUpload ? ' Upload files to get started.' : ''}`);

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      {!hideUploadArea &&
        (isTicketClosedOrCancelled ? (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
            <LockClosedIcon className="mx-auto h-12 w-12 text-gray-400" />
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-700">
                This ticket is {ticketState?.replace(/_/g, ' ')}. Attachments cannot be added to closed, cancelled, or resolved tickets.
              </p>
            </div>
          </div>
        ) : !canUpload ? (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
            <LockClosedIcon className="mx-auto h-12 w-12 text-gray-400" />
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-700">
                As the ticket is not assigned to you, you cannot upload attachments
              </p>
            </div>
          </div>
        ) : (
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isDragging
                ? 'border-indigo-500 bg-indigo-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
          >
            <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
            <div className="mt-4">
              <label
                htmlFor="file-upload"
                className="cursor-pointer text-indigo-600 hover:text-indigo-500 font-medium"
              >
                Choose files
              </label>
              <input
                ref={fileInputRef}
                id="file-upload"
                type="file"
                className="hidden"
                multiple
                onChange={handleFileSelect}
                disabled={uploadMutation.isPending}
              />
              <span className="text-gray-500"> or drag and drop</span>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Up to 25MB per file. Executables and scripts not allowed.
            </p>
            {uploadMutation.isPending && (
              <div className="mt-4">
                <div className="animate-pulse text-indigo-600">Uploading...</div>
              </div>
            )}
            {uploadError && (
              <div className="mt-4 text-sm text-red-600 bg-red-50 px-4 py-2 rounded">
                {uploadError}
                <button
                  onClick={() => setUploadError(null)}
                  className="ml-2 text-red-800 hover:text-red-900"
                >
                  <XMarkIcon className="h-4 w-4 inline" />
                </button>
              </div>
            )}
          </div>
        ))}

      {/* Attachments List */}
      <div className={`bg-white overflow-hidden ${hideUploadArea ? 'border border-gray-200 rounded-lg' : 'shadow rounded-lg'}`}>
        {isLoading ? (
          <div className="p-6 text-center text-gray-500">Loading attachments...</div>
        ) : isError ? (
          <div className="p-6 text-center text-sm text-red-600">Unable to load attachments.</div>
        ) : data?.attachments && data.attachments.length > 0 ? (
          <ul className="divide-y divide-gray-200">
            {data.attachments.map((attachment) => (
              <li key={attachment.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <DocumentIcon className="h-8 w-8 text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${hideUploadArea ? 'text-blue-800' : 'text-gray-900'}`}>
                        {attachment.originalName}
                      </p>
                      <p className="text-sm text-gray-500">
                        {formatFileSize(attachment.size)} • Uploaded by{' '}
                        {attachment.User ? `${attachment.User.firstName} ${attachment.User.lastName}` : 'Unknown User'} •{' '}
                        {formatDate(attachment.createdAt)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      type="button"
                      onClick={() => handlePreviewClick(attachment)}
                      className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                      title={isPreviewable(attachment.mimeType) ? "Preview" : "View Details"}
                    >
                      <EyeIcon className="h-5 w-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDownload(attachment.id, attachment.originalName, attachment.downloadUrl)}
                      className="p-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-full transition-colors"
                      title="Download"
                    >
                      <ArrowDownTrayIcon className="h-5 w-5" />
                    </button>
                    {canDelete && (
                      <button
                        type="button"
                        onClick={() => handleDeleteClick(attachment)}
                        disabled={deleteMutation.isPending}
                        className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors disabled:opacity-50"
                        title="Delete"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="p-6 text-center text-gray-500">{resolvedEmptyMessage}</div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Attachment"
        message={`Are you sure you want to delete "${attachmentToDelete?.originalName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmButtonClass="bg-red-600 hover:bg-red-700 text-white"
        isLoading={deleteMutation.isPending}
      />

      {/* Preview Modal */}
      <Transition appear show={previewModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={handlePreviewClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-75" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-6xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex justify-between items-start mb-4">
                    <Dialog.Title as="h3" className="text-lg font-medium text-gray-900">
                      {attachmentToPreview?.originalName}
                    </Dialog.Title>
                    <button
                      onClick={handlePreviewClose}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="mt-4">
                    {previewLoading ? (
                      <div className="flex items-center justify-center h-[70vh]">
                        <div className="animate-pulse text-gray-500">Loading preview...</div>
                      </div>
                    ) : (
                      renderPreview()
                    )}
                  </div>

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        if (attachmentToPreview) {
                          handleDownload(attachmentToPreview.id, attachmentToPreview.originalName, attachmentToPreview.downloadUrl);
                        }
                      }}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                    >
                      Download
                    </button>
                    <button
                      type="button"
                      onClick={handlePreviewClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default AttachmentTab;
